import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { emit } from "@/lib/event-bus";
import { isSimulatorEditorPath } from "@/lib/simulator-routes";
import {
  computeDisplayTabs,
  computeDuplicateName,
  computeNextTab,
  computeSavedTabs,
  nextOrder,
  resolveActiveTab,
  sortByOrder,
} from "@/lib/strategy/tabs";
import type { StrategyLayer } from "@/types/strategy";
import { useSimulatorStore } from "@/stores/simulatorStore";
import { simulatorTabMemory } from "@/stores/simulatorTabMemory";
import { useEventListener } from "./useEventBus";
import { useNamingLabels } from "./useLabels";
import { useTemplates } from "./useTemplates";

export function useStrategyTabs() {
  const namingLabels = useNamingLabels();
  const {
    loading,
    templates,
    archived,
    drafts,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    archiveTemplate,
    batchCreate,
    batchUpdate,
    batchDelete,
    reorder,
    revalidate,
  } = useTemplates();

  const [searchParams, setSearchParamsRaw] = useSearchParams();
  const activeTabId = searchParams.get("template");
  const setActiveTabIdRef = useRef(setSearchParamsRaw);
  // eslint-disable-next-line react-hooks/refs
  setActiveTabIdRef.current = setSearchParamsRaw;
  const setActiveTabId = useCallback(
    (id: string | null) =>
      setActiveTabIdRef.current(
        (current) => {
          const params = new URLSearchParams(current);
          if (id) {
            params.set("template", id);
          } else {
            params.delete("template");
          }
          return params;
        },
        { replace: true },
      ),
    [],
  );
  const seededRef = useRef(false);
  const { pathname } = useLocation();
  const onSimulator = isSimulatorEditorPath(pathname);

  // ── Seed a draft if user has 0 templates ──
  // Only do this on the simulator/editor route. Other pages can browse
  // without silently creating draft tabs in the shared template store.
  useEffect(() => {
    if (loading || seededRef.current || !onSimulator) return;
    seededRef.current = true;
    if (templates.length === 0) {
      saveTemplate({
        name: "",
        layers: [],
        scenario: { draft: true, order: 1 },
      }).then((t) => {
        if (t) setActiveTabId(t.id);
      });
    }
  }, [loading, templates.length, saveTemplate, setActiveTabId, onSimulator]);

  // ── URL ↔ memory: single source of truth on /simulator ──
  // - When `?template=` is empty AND templates exist, populate it from memory
  //   (last selected tab) or fall back to templates[last].
  // - When `?template=` has a valid value, remember it for next time.
  // - When `?template=` points at a stale/missing tab, repair it to a valid
  //   tab once the template list has loaded.
  // Pathname-scoped so we never write `?template=` to other routes' URLs.
  // Backtest and Manifold also use `?template=` for the active strategy, so
  // navigating between routes preserves the same selection.
  useEffect(() => {
    if (!onSimulator) return;
    if (activeTabId) {
      if (templates.some((t) => t.id === activeTabId)) {
        simulatorTabMemory.getState().set(activeTabId);
        return;
      }
      if (loading || templates.length === 0) return;
      const remembered = simulatorTabMemory.getState().value;
      const target =
        templates.find((t) => t.id === remembered) ??
        templates[templates.length - 1];
      setActiveTabId(target.id);
      return;
    }
    if (loading || templates.length === 0) return;
    const remembered = simulatorTabMemory.getState().value;
    const target =
      templates.find((t) => t.id === remembered) ??
      templates[templates.length - 1];
    setActiveTabId(target.id);
  }, [onSimulator, activeTabId, loading, templates, setActiveTabId]);

  // Internal-only: defensive resolution for action callbacks (closeTab, hotkeys).
  // Steady state has activeTabId == resolvedActiveId via the URL-init effect above.
  const resolvedActiveId = resolveActiveTab(activeTabId, loading, templates);
  const displayTabs = useMemo(
    () => computeDisplayTabs(templates, loading, namingLabels),
    [loading, templates, namingLabels],
  );
  const savedTabs = useMemo(
    () => computeSavedTabs(templates, archived, namingLabels),
    [templates, archived, namingLabels],
  );

  // ── Helper: activate next tab after removing one ──
  const activateNext = useCallback(
    (removedId: string) => {
      const sorted = sortByOrder(templates);
      const next = computeNextTab(removedId, resolvedActiveId, sorted);
      if (next !== resolvedActiveId) {
        setActiveTabId(next);
      }
      if (!next) {
        // Last tab closed — create a new empty draft
        saveTemplate({
          name: "",
          layers: [],
          scenario: { draft: true, order: 1 },
        }).then((t) => {
          if (t) setActiveTabId(t.id);
        });
      }
    },
    [resolvedActiveId, templates, saveTemplate, setActiveTabId],
  );

  // ── Tab actions ──

  const switchTab = useCallback(
    (targetId: string) => {
      if (targetId !== resolvedActiveId) setActiveTabId(targetId);
    },
    [resolvedActiveId, setActiveTabId],
  );

  const createDraft = useCallback(async (): Promise<string | null> => {
    // Inherit current active ticker so new tabs feel continuous with the
    // user's last selection. First-ever tab falls through to the column
    // default of 'SPX' on the API side.
    const ticker = useSimulatorStore.getState().activeTicker;
    const t = await saveTemplate({
      name: "",
      layers: [],
      scenario: { draft: true, order: nextOrder(templates) },
      ticker,
    });
    if (t) {
      setActiveTabId(t.id);
      return t.id;
    }
    return null;
  }, [saveTemplate, templates, setActiveTabId]);

  const deleteDraft = useCallback(
    async (id: string) => {
      await deleteTemplate(id);
      activateNext(id);
    },
    [deleteTemplate, activateNext],
  );

  const closeTab = useCallback(
    async (id: string, save?: boolean) => {
      const tmpl = templates.find((t) => t.id === id);
      if (!tmpl) return;

      if (tmpl.scenario.has_changes) {
        // Cancel any pending debounced auto-save so it can't fire after the
        // close write and clobber the snapshot we're about to persist. Drop
        // the cached unsaved layers so a future reopen seeds from the
        // template, not from the stale in-memory edits. Mirrors what
        // revertChanges / saveChanges do for the in-page case.
        emit("autosave:cancel");
        useSimulatorStore.getState().clearCachedLayers(id);

        if (save) {
          await updateTemplate(id, {
            scenario: {
              ...tmpl.scenario,
              has_changes: false,
              saved_state: undefined,
            },
          });
        } else {
          const snap = tmpl.scenario.saved_state;
          // Empty snapshot encodes the freshly-anchored "discard means
          // delete" state — there's no clean version to revert to. The
          // close-tab modal routes that case to deleteTab, but guard here
          // defensively so a direct closeTab(id, false) call doesn't
          // attempt a layers:[] write to a non-draft template.
          if (snap && snap.layers.length > 0) {
            await updateTemplate(id, {
              layers: snap.layers,
              scenario: {
                ...tmpl.scenario,
                has_changes: false,
                saved_state: undefined,
              },
            });
          } else {
            await updateTemplate(id, {
              scenario: {
                ...tmpl.scenario,
                has_changes: false,
                saved_state: undefined,
              },
            });
          }
        }
      }

      await archiveTemplate(id);
      activateNext(id);
    },
    [templates, updateTemplate, archiveTemplate, activateNext],
  );

  // Anchor a draft (assign a name + clear draft flag) and close the tab in
  // one step. Used by the "Save" branch of the close-tab warning
  // when the tab is a draft with picker selections.
  const anchorTabAndClose = useCallback(
    async (id: string, name: string) => {
      const tmpl = templates.find((t) => t.id === id);
      if (!tmpl) return;
      await updateTemplate(id, {
        name,
        scenario: { ...tmpl.scenario, draft: false, has_changes: false },
      });
      await archiveTemplate(id);
      activateNext(id);
    },
    [templates, updateTemplate, archiveTemplate, activateNext],
  );

  const closeTabs = useCallback(
    async (ids: string[]) => {
      const archivedAt = new Date().toISOString();
      const updates = ids.map((id) => {
        const tmpl = templates.find((t) => t.id === id);
        const snap = tmpl?.scenario.has_changes
          ? tmpl.scenario.saved_state
          : undefined;
        return {
          id,
          archived_at: archivedAt,
          ...(snap
            ? {
                layers: snap.layers,
                scenario: {
                  ...tmpl!.scenario,
                  has_changes: false,
                  saved_state: undefined,
                },
              }
            : {}),
        };
      });
      await batchUpdate(updates, (t) => ({ ...t, archived_at: archivedAt }));
      // Activate next tab or seed a new draft
      const idSet = new Set(ids);
      const remaining = templates.filter((t) => !idSet.has(t.id));
      if (remaining.length > 0) {
        const sorted = sortByOrder(remaining);
        setActiveTabId(sorted[sorted.length - 1].id);
      } else {
        const t = await saveTemplate({
          name: "",
          layers: [],
          scenario: { draft: true, order: 1 },
        });
        if (t) setActiveTabId(t.id);
      }
    },
    [templates, batchUpdate, saveTemplate, setActiveTabId],
  );

  const reopenTab = useCallback(
    async (id: string) => {
      const tmpl = [...templates, ...archived].find((t) => t.id === id);
      const order = nextOrder([...templates, ...archived]);
      await updateTemplate(id, {
        archived_at: null,
        scenario: {
          ...tmpl?.scenario,
          draft: tmpl?.scenario.draft ?? false,
          order,
        },
      });
      setActiveTabId(id);
    },
    [templates, archived, updateTemplate, setActiveTabId],
  );

  const reopenTabs = useCallback(
    async (ids: string[]) => {
      const all = [...templates, ...archived];
      let order = nextOrder(all);
      const updates = ids.map((id) => {
        const tmpl = all.find((t) => t.id === id);
        return {
          id,
          archived_at: null,
          scenario: {
            ...tmpl?.scenario,
            draft: tmpl?.scenario.draft ?? false,
            order: order++,
          },
        };
      });
      const orderMap = new Map(updates.map((u) => [u.id, u.scenario]));
      await batchUpdate(updates, (t) => ({
        ...t,
        archived_at: null,
        scenario: orderMap.get(t.id) ?? t.scenario,
      }));
      setActiveTabId(ids[ids.length - 1]);
    },
    [templates, archived, batchUpdate, setActiveTabId],
  );

  const deleteTabs = useCallback(
    async (ids: string[]) => {
      await batchDelete(ids);
      // If active tab was deleted, activate next
      const idSet = new Set(ids);
      if (resolvedActiveId && idSet.has(resolvedActiveId)) {
        const remaining = templates.filter((t) => !idSet.has(t.id));
        if (remaining.length > 0) {
          const sorted = sortByOrder(remaining);
          setActiveTabId(sorted[sorted.length - 1].id);
        } else {
          const t = await saveTemplate({
            name: "",
            layers: [],
            scenario: { draft: true, order: 1 },
          });
          if (t) setActiveTabId(t.id);
        }
      }
    },
    [batchDelete, resolvedActiveId, templates, saveTemplate, setActiveTabId],
  );

  const deleteTab = useCallback((id: string) => deleteTabs([id]), [deleteTabs]);

  const duplicateTab = useCallback(
    async (id: string): Promise<string | null> => {
      const all = [...templates, ...archived];
      const source = all.find((t) => t.id === id);
      if (!source) return null;

      const snap = source.scenario.saved_state;
      const layers = snap ? snap.layers : source.layers;
      const dupName = computeDuplicateName(source.name, all);

      const t = await saveTemplate({
        name: dupName,
        layers,
        scenario: { draft: false, order: nextOrder(all) },
        archived_at: new Date().toISOString(),
        ticker: source.ticker,
      });
      return t?.id ?? null;
    },
    [templates, archived, saveTemplate],
  );

  const duplicateTabs = useCallback(
    async (ids: string[]): Promise<string[]> => {
      const all = [...templates, ...archived];
      const sources = ids
        .map((id) => all.find((t) => t.id === id))
        .filter(Boolean) as typeof all;
      if (sources.length === 0) return [];

      // Pre-compute names, accumulating so each duplicate sees previous ones
      const namePool = [...all];
      let order = nextOrder(all);
      const inputs = sources.map((source) => {
        const dupName = computeDuplicateName(source.name, namePool);
        namePool.push({ ...source, name: dupName });
        const snap = source.scenario.saved_state;
        const layers = snap ? snap.layers : source.layers;
        return {
          name: dupName,
          layers,
          scenario: { draft: false, order: order++ },
          archived_at: new Date().toISOString(),
        };
      });

      const created = await batchCreate(inputs);
      return created.map((t) => t.id);
    },
    [templates, archived, batchCreate],
  );

  const createTabFromLayers = useCallback(
    async (
      layers: StrategyLayer[],
      ticker?: string,
    ): Promise<string | null> => {
      if (layers.length === 0) return null;
      const all = [...templates, ...archived];
      // Persist as unnamed; the tab strip + every read site resolves the
      // display name via the auto-namer. Promoting an auto-derived string to
      // the stored `name` would put a name on the template the user never
      // typed.
      const t = await saveTemplate({
        name: "",
        layers,
        scenario: { draft: false, has_changes: false, order: nextOrder(all) },
        ticker: ticker ?? useSimulatorStore.getState().activeTicker,
      });
      if (t) {
        setActiveTabId(t.id);
        return t.id;
      }
      return null;
    },
    [templates, archived, saveTemplate, setActiveTabId],
  );

  const reorderTabs = useCallback(
    async (orderedIds: string[]) => {
      // Skip the round-trip if the visible order already matches.
      const sorted = sortByOrder(templates).map((t) => t.id);
      const same =
        sorted.length === orderedIds.length &&
        sorted.every((id, i) => id === orderedIds[i]);
      if (same) return;
      await reorder(orderedIds);
    },
    [templates, reorder],
  );

  const combineStrategies = useCallback(
    async (ids: string[]): Promise<boolean> => {
      const all = [...templates, ...archived];
      const sources = ids
        .map((id) => all.find((t) => t.id === id))
        .filter(Boolean) as typeof all;
      if (sources.length < 2) return false;

      let layerIndex = 0;
      const combinedLayers = sources.flatMap((src) => {
        const snap = src.scenario.saved_state;
        const layers = snap ? snap.layers : src.layers;
        return layers.map((layer) => ({
          ...layer,
          id: `layer-${layer.kind}-${layerIndex++}`,
        }));
      });

      // Persist as unnamed — see createTabFromLayers for the rationale.
      const t = await saveTemplate({
        name: "",
        layers: combinedLayers,
        scenario: { draft: false, has_changes: false, order: nextOrder(all) },
      });
      if (t) {
        setActiveTabId(t.id);
        return true;
      }
      return false;
    },
    [templates, archived, saveTemplate, setActiveTabId],
  );

  // ── Hotkeys ──

  useEventListener("hotkey:tab-new", createDraft);

  useEventListener("hotkey:tab-duplicate", () => {
    if (resolvedActiveId) duplicateTab(resolvedActiveId);
  });

  useEventListener("hotkey:tab-prev", () => {
    const sorted = sortByOrder(templates);
    if (sorted.length < 2) return;
    const idx = sorted.findIndex((t) => t.id === resolvedActiveId);
    switchTab(sorted[idx <= 0 ? sorted.length - 1 : idx - 1].id);
  });

  useEventListener("hotkey:tab-next", () => {
    const sorted = sortByOrder(templates);
    if (sorted.length < 2) return;
    const idx = sorted.findIndex((t) => t.id === resolvedActiveId);
    switchTab(sorted[idx >= sorted.length - 1 ? 0 : idx + 1].id);
  });

  useEventListener("hotkey:tab-index", (index) => {
    const sorted = sortByOrder(templates);
    if (index < sorted.length) switchTab(sorted[index].id);
  });

  return {
    loading,
    activeTabId,
    templates,
    archived,
    drafts,
    displayTabs,
    savedTabs,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    deleteTab,
    switchTab,
    createDraft,
    deleteDraft,
    closeTab,
    closeTabs,
    anchorTabAndClose,
    reopenTab,
    reopenTabs,
    duplicateTab,
    duplicateTabs,
    deleteTabs,
    reorderTabs,
    combineStrategies,
    createTabFromLayers,
    revalidate,
  };
}
